// client/src/components/BinaryRainCanvas.tsx
// Matrix-style binary rain animation. Extracted from LandingPage for reuse.

import React, { useEffect, useRef } from 'react';

interface BinaryRainCanvasProps {
  opacity?: number;
  style?: React.CSSProperties;
}

export default function BinaryRainCanvas({ opacity = 0.2, style }: BinaryRainCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const FONT_SIZE = 13;
    const CHARS = '01010110010110010100110100110101';
    const BRAND_COLORS = [
      'rgba(122,162,255,0.9)',
      'rgba(100,210,255,0.9)',
      'rgba(158,184,255,0.85)',
      'rgba(85,133,232,0.85)',
    ];

    let width = 0;
    let height = 0;
    let cols = 0;
    let drops: number[] = [];
    let streamColors: string[] = [];

    const init = (w: number, h: number) => {
      width = w;
      height = h;
      canvas.width = w;
      canvas.height = h;
      cols = Math.floor(w / FONT_SIZE);
      drops = Array.from({ length: cols }, () =>
        Math.floor(Math.random() * -(h / FONT_SIZE)),
      );
      streamColors = Array.from(
        { length: cols },
        () => BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)],
      );
    };

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { inlineSize: w, blockSize: h } = entry.contentBoxSize
        ? entry.contentBoxSize[0]
        : { inlineSize: entry.contentRect.width, blockSize: entry.contentRect.height };
      init(Math.floor(w), Math.floor(h));
    });
    ro.observe(canvas);

    let animId: number;

    const draw = () => {
      ctx.fillStyle = 'rgba(11,13,16,0.055)';
      ctx.fillRect(0, 0, width, height);

      ctx.font = `${FONT_SIZE}px 'Courier New', Courier, monospace`;
      ctx.textAlign = 'left';

      for (let i = 0; i < cols; i++) {
        const y = drops[i];
        if (y < 0) {
          drops[i] += 0.35;
          continue;
        }

        const px = i * FONT_SIZE;
        const py = Math.floor(y) * FONT_SIZE;

        if (py <= height) {
          ctx.fillStyle = 'rgba(215,230,255,0.95)';
          ctx.fillText(CHARS[Math.floor(Math.random() * CHARS.length)], px, py);

          if (py - FONT_SIZE >= 0) {
            ctx.fillStyle = streamColors[i];
            ctx.fillText(CHARS[Math.floor(Math.random() * CHARS.length)], px, py - FONT_SIZE);
          }
        }

        drops[i] += 0.42;

        if (drops[i] * FONT_SIZE > height + FONT_SIZE) {
          if (Math.random() > 0.972) {
            drops[i] = Math.floor(Math.random() * -(height / FONT_SIZE / 2));
            streamColors[i] =
              BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)];
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}
